// ABOUTME: Base skeleton components for loading states.
// ABOUTME: Provides reusable skeleton primitives with consistent styling.

import type { FC } from "react"

interface CSkeletonProps {
  className?: string
}

/**
 * Base skeleton block with pulse animation
 */
export const CSkeleton: FC<CSkeletonProps> = ({ className = "" }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
)

/**
 * Skeleton for text lines
 */
export const CSkeletonText: FC<{ lines?: number; className?: string }> = ({ lines = 1, className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <CSkeleton
        key={i}
        className={`h-4 ${i === lines - 1 && lines > 1 ? "w-3/4" : "w-full"}`}
      />
    ))}
  </div>
)

/**
 * Skeleton for avatar/circle elements
 */
export const CSkeletonAvatar: FC<{ size?: "sm" | "md" | "lg"; className?: string }> = ({
  size = "md",
  className = "",
}) => {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16",
  }
  return <CSkeleton className={`rounded-full ${sizeClasses[size]} ${className}`} />
}

/**
 * Skeleton for card/box elements
 */
export const CSkeletonCard: FC<CSkeletonProps> = ({ className = "" }) => (
  <div className={`animate-pulse bg-white rounded-lg p-4 border border-gray-200 ${className}`}>
    <CSkeleton className="h-6 w-1/3 mb-3" />
    <CSkeletonText lines={2} />
  </div>
)

/**
 * Skeleton for list items
 */
export const CSkeletonListItem: FC<{ showAvatar?: boolean; className?: string }> = ({
  showAvatar = false,
  className = "",
}) => (
  <div className={`flex items-center gap-3 p-3 ${className}`}>
    {showAvatar && <CSkeletonAvatar size="sm" />}
    <div className="flex-1">
      <CSkeleton className="h-4 w-1/2 mb-2" />
      <CSkeleton className="h-3 w-1/3" />
    </div>
  </div>
)

/**
 * Skeleton for list with multiple items
 */
export const CSkeletonList: FC<{
  count?: number
  showAvatar?: boolean
  className?: string
}> = ({ count = 3, showAvatar = false, className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: count }).map((_, i) => (
      <CSkeletonListItem
        key={i}
        showAvatar={showAvatar}
      />
    ))}
  </div>
)

/**
 * Skeleton for subscription/payment card
 */
export const CSkeletonSubscriptionCard: FC<CSkeletonProps> = ({ className = "" }) => (
  <div
    className={`flex flex-col md:flex-row md:justify-between rounded-lg p-5 bg-white border border-gray-200 ${className}`}
  >
    <div className="md:w-[60%] space-y-3">
      <CSkeleton className="h-6 w-1/3" />
      <CSkeleton className="h-4 w-1/4" />
      <CSkeleton className="h-px w-full bg-gray-200" />
      <CSkeletonText lines={3} />
      <CSkeleton className="h-9 w-full md:w-[305px] mt-4" />
    </div>
    <div className="md:w-[345px] p-5 rounded-lg bg-gray-50 mt-5 md:mt-0">
      <CSkeleton className="h-12 w-2/3 mb-4" />
      <CSkeleton className="h-10 w-full" />
    </div>
  </div>
)

/**
 * Skeleton for trainer/coach item in settings
 */
export const CSkeletonTrainerItem: FC<CSkeletonProps> = ({ className = "" }) => (
  <div className={`flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg ${className}`}>
    <CSkeleton className="h-4 w-1/3" />
    <CSkeleton className="h-8 w-24" />
  </div>
)

/**
 * Skeleton for statistics card
 */
export const CSkeletonStatCard: FC<CSkeletonProps> = ({ className = "" }) => (
  <div className={`bg-white rounded-lg p-4 border border-gray-200 ${className}`}>
    <CSkeleton className="h-4 w-1/2 mb-3" />
    <CSkeleton className="h-8 w-1/3 mb-2" />
    <CSkeleton className="h-3 w-1/4" />
  </div>
)

/**
 * Skeleton for history list items (value + date + delete button)
 */
export const CSkeletonHistoryList: FC<{ count?: number; className?: string }> = ({ count = 3, className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="flex items-center justify-between p-3 bg-customGray-100 rounded-lg animate-pulse"
      >
        <div>
          <CSkeleton className="h-4 w-20 mb-1.5" />
          <CSkeleton className="h-3 w-16" />
        </div>
        <CSkeleton className="h-5 w-5 rounded" />
      </div>
    ))}
  </div>
)
